import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { fetchBudget } from '@/api/budget'
import { ROUTES } from '@/routes/routes'
import { Button } from '@/components/ui/button'
import { CategoryIcon } from '@/lib/categoryIcon'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { cn } from '@/lib/utils'
import { budgetStatus, type BudgetStatus } from './monthProgress'
import { SECTION_CLASS, SectionLink } from './SectionLink'

const TOP_N = 3

const STATUSES: { status: BudgetStatus; label: string; short: string }[] = [
  { status: 'over', label: 'Over', short: 'Over' },
  { status: 'almost', label: 'Almost used', short: 'Almost' },
  { status: 'within', label: 'Within', short: 'Within' },
]

const PILL: Record<BudgetStatus, string> = {
  over: 'bg-[var(--fl-error-bg)] text-[var(--fl-error-dark)]',
  almost: 'bg-[var(--fl-warning-bg)] text-[var(--fl-warning)]',
  within: 'bg-[var(--fl-income-bg)] text-[var(--fl-income-text)]',
}
const DOT: Record<BudgetStatus, string> = {
  over: 'bg-[var(--fl-error)]',
  almost: 'bg-[var(--fl-warning-text)]',
  within: 'bg-[var(--fl-income)]',
}
const TEXT: Record<BudgetStatus, string> = {
  over: 'text-[var(--fl-error)]',
  almost: 'text-[var(--fl-warning)]',
  within: 'text-[var(--fl-text-1)]',
}

export function BudgetPace({
  pacePercent,
  format,
  className,
}: {
  pacePercent: number
  format: (n: number) => string
  className?: string
}) {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['budget', 'MONTHLY', 'current'],
    queryFn: () => fetchBudget({ periodType: 'MONTHLY' }),
    refetchOnMount: 'always',
  })

  const sorted = [...(data?.categories ?? [])].sort((a, b) => b.percentUsed - a.percentUsed)
  const more = sorted.length - TOP_N
  const counts = STATUSES.map((s) => ({
    ...s,
    count: sorted.filter((cat) => budgetStatus(cat.percentUsed) === s.status).length,
  }))

  return (
    <section className={cn(SECTION_CLASS, className)}>
      <SectionLink to={ROUTES.APP.BUDGET} label="Budgets" />
      {isPending ? (
        <div className="mt-1 space-y-4 animate-pulse" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-7 rounded bg-[var(--fl-surface-subtle)]" />
          ))}
        </div>
      ) : isError ? (
        <p className="pt-2 text-sm text-[var(--fl-error-dark)]">
          Couldn't load budgets.{' '}
          <Button
            onClick={() => refetch()}
            variant="link"
            className="h-auto p-0 font-medium underline text-[var(--fl-error-dark)]"
          >
            Retry
          </Button>
        </p>
      ) : sorted.length === 0 ? (
        <p className="pt-2 text-[15px] text-[var(--fl-text-2)]">
          Set a monthly limit per category to see it here.{' '}
          <Link to={ROUTES.APP.BUDGET} className="font-medium text-[var(--fl-primary)] underline">
            Set budgets
          </Link>
        </p>
      ) : (
        <>
          <p className="mt-1 flex flex-wrap gap-1.5" aria-label="Budgets by status">
            {counts.map(({ status, label, short, count }) => (
              <span
                key={status}
                data-testid={`budget-count-${status}`}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums',
                  PILL[status]
                )}
              >
                <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full', DOT[status])} />
                <span className="lg:hidden">{short}</span>
                <span className="hidden lg:inline">{label}</span> {count}
              </span>
            ))}
          </p>
          <ul className="mt-2 flex flex-col gap-1.5 lg:gap-2">
            {sorted.slice(0, TOP_N).map((cat) => {
              const status = budgetStatus(cat.percentUsed)
              const left = fromPaise(toPaise(cat.limit) - toPaise(cat.spent))
              return (
                <li key={cat.categoryId} className="flex items-center gap-2.5">
                  <span className="hidden h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[var(--fl-primary-subtle)] text-[var(--fl-text-2)] lg:flex">
                    <CategoryIcon icon={cat.icon ?? ''} size={13} />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-[var(--fl-text-1)] lg:text-[15px]">
                    {cat.name}
                  </span>
                  <span
                    className={cn(
                      'hidden whitespace-nowrap text-[13px] tabular-nums lg:inline',
                      left < 0 ? 'text-[var(--fl-error)]' : 'text-[var(--fl-text-2)]'
                    )}
                  >
                    {left < 0 ? `${format(-left)} over` : `${format(left)} left`}
                  </span>
                  <span className="relative h-1.5 w-20 shrink-0 rounded-full bg-[var(--fl-border)] lg:w-24">
                    <span
                      className={cn('block h-full rounded-full', DOT[status])}
                      style={{ width: `${Math.min(cat.percentUsed, 100)}%` }}
                    />
                    <span
                      data-testid="pace-tick"
                      aria-hidden="true"
                      className="absolute -top-1 h-3.5 w-0.5 rounded-sm bg-[var(--fl-text-2)]"
                      style={{ left: `${pacePercent}%` }}
                    />
                  </span>
                  <span
                    className={cn('w-11 text-right text-sm font-bold tabular-nums', TEXT[status])}
                  >
                    {Math.round(cat.percentUsed)}%
                  </span>
                </li>
              )
            })}
          </ul>
          <div className="mt-2.5 flex items-center justify-between text-xs text-[var(--fl-text-2)]">
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-0.5 rounded-sm bg-[var(--fl-text-2)]" aria-hidden="true" />
              Today · {Math.round(pacePercent)}% of month gone
            </span>
            {more > 0 && (
              <Link
                to={ROUTES.APP.BUDGET}
                className="text-[13px] font-semibold text-[var(--fl-primary)]"
              >
                + {more} more
              </Link>
            )}
          </div>
        </>
      )}
    </section>
  )
}
